/**
 * media.js — browser media utilities
 */

/**
 * Probe audio/video duration by reading file metadata without decoding the whole file.
 * Uses a hidden <audio>/<video> element with an object URL — resolves in milliseconds.
 * Falls back to 0 if the format isn't decodable by the browser.
 *
 * @param {File} file
 * @returns {Promise<number>} duration in seconds, or 0 on failure
 */
export function probeDuration(file) {
  return new Promise((resolve) => {
    const tag = file.type.startsWith('video/') ? 'video' : 'audio';
    const el = document.createElement(tag);
    const url = URL.createObjectURL(file);
    el.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve(isFinite(el.duration) ? el.duration : 0);
    };
    el.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(0);
    };
    el.src = url;
  });
}

/**
 * Grab one frame of a video as a JPEG Blob, for the Library poster.
 * Catalyst functions have no ffmpeg, so the browser is the only place a frame
 * can be captured. Resolves null on any failure — the card falls back to a
 * placeholder, which is fine.
 *
 * @param {File} file
 * @param {number} [atSeconds=1]
 * @returns {Promise<Blob|null>}
 */
export function captureVideoPoster(file, atSeconds = 1) {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    const url = URL.createObjectURL(file);
    const done = (blob) => { URL.revokeObjectURL(url); resolve(blob); };
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.onloadedmetadata = () => {
      video.currentTime = Math.min(atSeconds, (video.duration || atSeconds) / 2);
    };
    video.onseeked = () => {
      try {
        const scale = Math.min(1, 640 / (video.videoWidth || 640));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round((video.videoWidth || 640) * scale);
        canvas.height = Math.round((video.videoHeight || 360) * scale);
        canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => done(blob), 'image/jpeg', 0.75);
      } catch (_) {
        done(null);
      }
    };
    video.onerror = () => done(null);
    setTimeout(() => done(null), 10000);
    video.src = url;
  });
}
